import { Form, Field } from "react-final-form";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import CTFieldValidator, {
  email,
  required,
  phoneNumber,
  minLength,
  composeValidators,
  validEmail,
  validName,
  validPhone,
  minLengthCurrier,
} from "@Components/forms/helpers/FieldValidator";
import { Button, Select, Textarea } from "@Components/ui";
import { CONTACT_US_FORM_TYPE } from "@Constants/common";
import { IContactUsForm } from "@Types/contact-us-page";
import { isCanadianLocale } from "@Utils/locale";
import { useContactUs } from "@wff/context";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import { validateAllTheThings } from "../../components/forms/helpers/FormValidator";
import classes from "./ContactUs.module.scss";

export const REASON_FOR_CONTACT_SELECT_ID = "reason-for-contact-select";
export const IS_A_MEMBER_SELECT_ID = "is-a-member-id";

export interface ContactUsFormValues {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber?: string;
  orderNumber?: string;
  contactReason: string;
  unlimitedMember: string;
  detailQuery: string;
}

export interface IContactUsFormPayload extends ContactUsFormValues {}

interface Props extends IContactUsForm {
  onSubmit: (formValues: ContactUsFormValues) => void;
}

export const phoneFieldValidators = [required(), phoneNumber(), minLength(10)];

export const contactUSFieldValidators = {
  email: (value: string) => {
    return composeValidators(value, [validEmail, minLengthCurrier(2)]);
  },
  firstName: (value: string) => {
    return composeValidators(value, [validName, minLengthCurrier(2)]);
  },
  lastName: (value: string) => {
    return composeValidators(value, [validName, minLengthCurrier(2)]);
  },
  phone: (value: string) => {
    return composeValidators(value, [validPhone, minLengthCurrier(2)]);
  },
};
export const ContactUsForm = (props: Props) => {
  const { contactForm, setContactFormHandler } = useContactUs();
  const {
    contactFormReasonsForContactList,
    areYouAMemberList,
    contactFormHeading,
    contactFormFirstNameLabel,
    contactFormLastNameLabel,
    contactFormEmailLabel,
    contactFormPhoneNumberLabel,
    contactFormOrderNumberLabel,
    contactFormReasonForContactLabel,
    contactFormAreYouMemberLabel,
    contactFormQuestionPlaceholder,
    contactFormReplyMessage,
    contactFormSubmitButtonText,
    onSubmit,
  } = props;

  const onSubmitHandler = (values: ContactUsFormValues) => onSubmit(values);
  const { languageCode } = useAppSelector(localeSelector);
  const isCanadianUser = isCanadianLocale(languageCode);

  return (
    <div className={classes["contact-us-page__form"]}>
      <h3>{contactFormHeading}</h3>
      <Form
        onSubmit={onSubmitHandler}
        initialValues={{
          unlimitedMember: areYouAMemberList?.items?.[0]?.value,
          contactReason: contactFormReasonsForContactList?.items?.[0]?.value,
        }}
        render={({ handleSubmit, form }) => {
          return (
            <form>
              <FormTextField
                name="firstName"
                label={contactFormFirstNameLabel}
                onChange={(e) => {
                  setContactFormHandler(
                    e.target.value,
                    CONTACT_US_FORM_TYPE.FIRST_NAME
                  );
                }}
                validate={CTFieldValidator([required()], "FirstName")}
                outlined={true}
                className={classes["form-field"]}
                aria-label="firstName"
              />
              <FormTextField
                name="lastName"
                label={contactFormLastNameLabel}
                onChange={(e) => {
                  setContactFormHandler(
                    e.target.value,
                    CONTACT_US_FORM_TYPE.LAST_NAME
                  );
                }}
                validate={CTFieldValidator([required()], "Lastname")}
                outlined={true}
                className={classes["form-field"]}
                aria-label="lastName"
              />
              <FormTextField
                name="email"
                label={contactFormEmailLabel}
                onChange={(e) => {
                  setContactFormHandler(
                    e.target.value,
                    CONTACT_US_FORM_TYPE.EMAIL
                  );
                }}
                validate={CTFieldValidator([required(), email()], "Email")}
                outlined={true}
                className={classes["form-field"]}
                aria-label="email"
              />
              <FormTextField
                name="contactNumber"
                label={contactFormPhoneNumberLabel}
                onChange={(e) => {
                  setContactFormHandler(
                    e.target.value,
                    CONTACT_US_FORM_TYPE.PHONE
                  );
                }}
                validate={CTFieldValidator([...phoneFieldValidators])}
                outlined={true}
                className={classes["form-field"]}
                aria-label="contactNumber"
              />
              <FormTextField
                name="orderNumber"
                label={contactFormOrderNumberLabel}
                outlined={true}
                className={classes["form-field"]}
                aria-label="orderNumber"
              />
              <Field
                name="contactReason"
                render={({ input }) => (
                  <Select
                    {...input}
                    className={classes["form-field"]}
                    options={contactFormReasonsForContactList?.items}
                    selected={input.value}
                    label={contactFormReasonForContactLabel}
                    aria-label="contactReason"
                    data-testid={REASON_FOR_CONTACT_SELECT_ID}
                  />
                )}
              />
              <Field
                name="unlimitedMember"
                render={({ input }) => (
                  <Select
                    {...input}
                    className={classes["form-field"]}
                    options={areYouAMemberList?.items}
                    selected={input.value}
                    label={contactFormAreYouMemberLabel}
                    aria-label="unlimitedMember"
                    data-testid={IS_A_MEMBER_SELECT_ID}
                  />
                )}
              />
              <Field
                name="detailQuery"
                render={({ input }) => (
                  <div className={classes["form-field"]}>
                    <Textarea
                      placeholder={contactFormQuestionPlaceholder}
                      {...input}
                      aria-label="detailQuery"
                    />
                    <span className={classes["contact-us-page__form-message"]}>
                      {contactFormReplyMessage}
                    </span>
                  </div>
                )}
              />
              <Button
                className="action"
                disabled={
                  isCanadianUser &&
                  !validateAllTheThings(contactForm, contactUSFieldValidators)
                }
                onClick={(event) => handleSubmit(event)?.then(form.reset)}
              >
                {contactFormSubmitButtonText}
              </Button>
            </form>
          );
        }}
      />
    </div>
  );
};
